import React, { ChangeEvent, useEffect, useState } from "react";
import {
  Box,
  FormControl,
  FormControlLabel,
  Grid,
  InputAdornment,
  RadioGroup,
  MenuItem,
  InputLabel,
  Select,
} from "@mui/material";
import OrderedList from "./OrderedList";
import { DropResult } from "@edorivai/react-smooth-dnd";
import produce from "immer";
import { startCase, get } from "lodash";
import {
  Data,
  SurchargePercentage,
  SurchargeV2,
} from "../store/terminal/types";
import CustomizedSwitch from "./formElements/Switch";
import pageStyles from "./../pages/pageStyles";
import { FormikErrors, useFormikContext, FormikProps, FastField } from "formik";
import SectionWrapper from "./pageSections/SectionWrapper";
import OptionalToolTip from "./toolTip/OptionalToolTip";
import TextInput from "./formElements/TextInput";
import { ConfigPropExtenderHoc } from "./StaticConfigProvider";
import { connect, useSelector } from "react-redux";
import SelectDropDown from "./formElements/SelectDropDown";
import { ApplicationState } from "../store";
import CustomizedRadio from "./formElements/RadioButton";
import { SurchargeV2ToggleOptions } from "../pages/terminal/terminalEditForm/terminalEditFormComponents/editSurchargeTippingComponent/TerminalConstants";
import Tipping from "./Tipping";
import TippingV2 from "./TippingV2";
import { getValueForField } from "../pages/terminal/terminalEditForm/terminalEditFormComponents/editConfigurationComponent/ConfigurationOptions";
import InfoIcon from "@mui/icons-material/Info";

let cardTypeOptionsState: any = [];

interface V1ItemProps {
  surcharge: SurchargePercentage;
  index: number;
  onChange: (
    attribute: string
  ) => (event: ChangeEvent<{ value: unknown }>) => void;
  values?: any;
  errors?: FormikErrors<Data>;
  disabled?: boolean;
}

interface V2ItemProps {
  surcharge: SurchargeV2;
  index: number;
  onChange: (
    attribute: string
  ) => (event: ChangeEvent<{ value: unknown }>) => void;
  values?: any;
  errors?: FormikErrors<Data>;
  disabled?: boolean;
  isUpdateAllowed?: boolean;
}

const SurchargeListItem = ({ index, disabled }: V1ItemProps) => {
  const formProps = useFormikContext<FormikProps<Data>>();
  const values = formProps.values as unknown as Data;

  const { Surcharge } = values.attributes;

  return (
    <Grid container xs={12} md={12} spacing={2}>
      <Grid item xs={6} md={6}>
        <SelectDropDown
          key={`CardType_${Surcharge}`}
          label="Card Type"
          fieldname={`attributes.SurchargePercentage[${index}].CardType`}
          options={cardTypeOptionsState}
          disabled={disabled}
        />
      </Grid>
      <Grid item xs={3} md={3}>
        <TextInput
          key={`Surcharge_${Surcharge}`}
          fieldname={`attributes.SurchargePercentage[${index}].Percentage`}
          label="Surcharge"
          type="number"
          InputProps={{
            endAdornment: <InputAdornment position="end">%</InputAdornment>,
          }}
          disabled={disabled}
        />
      </Grid>
      <Grid item xs={3} md={3}>
        <TextInput
          key={`GST_${Surcharge}`}
          fieldname={`attributes.SurchargePercentage[${index}].SurchargeGST`}
          label="GST"
          type="number"
          InputProps={{
            endAdornment: <InputAdornment position="end">%</InputAdornment>,
          }}
          disabled={disabled}
        />
      </Grid>
    </Grid>
  );
};

const SurchargeListItemV2 = (props: V2ItemProps) => {
  const formProps = useFormikContext<FormikProps<Data>>();
  const values = formProps.values as unknown as Data;

  const { index, disabled } = props;

  const { SurchargeType } = values.attributes.SurchargeV2[index];
  const { EnableSurchargeV2 } = values.attributes;
  const { EnableSurchargeSettingsOnTerminal } = values.attributes;

  const isPercentage = SurchargeType === "Percentage";

  const endAdornment = (
    <InputAdornment position="end">{isPercentage ? "%" : "$"}</InputAdornment>
  );

  return (
    <Grid container xs={12} md={12} spacing={2}>
      <Grid item xs={6} md={6}>
        <SelectDropDown
          label="Card Type"
          fieldname={`attributes.SurchargeV2[${index}].CardType`}
          options={cardTypeOptionsState}
          disabled={disabled}
        />
      </Grid>
      <Grid item xs={6} md={6}>
        <FormControl fullWidth variant="outlined" disabled={disabled}>
          <InputLabel id="surchargetype" disabled={disabled}>
            Surcharge Type
          </InputLabel>
          <FastField
            as={Select}
            name={`attributes.SurchargeV2[${index}].SurchargeType`}
            labelId="surchargeType"
            label="Surcharge Type"
            variant="outlined"
            disabled={disabled}
            key={`SurchargeType_${EnableSurchargeV2}_${EnableSurchargeSettingsOnTerminal}`}
          >
            <MenuItem value="Percentage">Percentage</MenuItem>
            <MenuItem value="Amount">Amount</MenuItem>
          </FastField>
        </FormControl>
      </Grid>
      <Grid item xs={3} md={5}>
        <TextInput
          type="number"
          fieldname={`attributes.SurchargeV2[${index}].PurchaseSurchargeValue`}
          InputProps={{
            endAdornment,
          }}
          disabled={disabled}
          key={`PurchaseSurchargeValue_${SurchargeType}_${EnableSurchargeV2}_${EnableSurchargeSettingsOnTerminal}`}
          label="Purchase Surcharge"
        />
      </Grid>
      <Grid item xs={3} md={5}>
        <TextInput
          type="number"
          fieldname={`attributes.SurchargeV2[${index}].CashoutSurchargeValue`}
          InputProps={{
            endAdornment,
          }}
          disabled={disabled}
          key={`CashoutSurchargeValue_${SurchargeType}_${EnableSurchargeV2}_${EnableSurchargeSettingsOnTerminal}`}
          label="Cash Out Surcharge"
        />
      </Grid>
      <Grid item xs={3} md={2}>
        <TextInput
          fieldname={`attributes.SurchargeV2[${index}].GSTValue`}
          label="GST"
          type="number"
          InputProps={{
            endAdornment: <InputAdornment position="end">%</InputAdornment>,
          }}
          disabled={disabled}
          key={`GSTValue${EnableSurchargeV2}_${EnableSurchargeSettingsOnTerminal}`}
        />
      </Grid>
    </Grid>
  );
};

interface SurchargeProps {
  toolTips?: { terminals?: { options?: Record<string, string> } };
  isUpdateAllowed?: boolean;
}

const Surcharge = (props: SurchargeProps) => {
  const stateLoading = useSelector((state: ApplicationState) => state.cardType);
  const loading = stateLoading.loading;

  const stateData: any = useSelector(
    (state: ApplicationState) => state.cardType.data?.data
  ) ?? [{}];

  if (!loading) {
    cardTypeOptionsState = stateData.map((item: any) => {
      if (item && item.type) {
        return { [item.type]: startCase(item.type.toLowerCase()) };
      }
      return {};
    }).filter((option: any) => Object.keys(option).length > 0);
  }

  const formProps = useFormikContext<FormikProps<Data>>();
  const { setFieldValue, errors } = formProps;
  const values = formProps.values as unknown as Data;
  const classes = pageStyles();
  const { isUpdateAllowed } = props;

  const {
    SurchargePercentage,
    SurchargeV2,
    FixedSurchargeAmount,
    FixedSurchargePercentage,
  } = values.attributes;

  const isDisableSurcharge = Boolean(
    FixedSurchargeAmount || FixedSurchargePercentage
  );

  const handleSurchargeV2ToggleOptions = (
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    setFieldValue("attributes.SurchargeV2Toggle", e.target.value);
  };

  const V1ChangeListenerBuilder =
    (index: number, setFieldValue: (field: string, value: any) => void) =>
    (attributeName: string) =>
    (event: ChangeEvent<{ value: unknown }>) => {
      if (setFieldValue && index > -1) {
        const newItems = produce(
          SurchargePercentage,
          (draft: SurchargePercentage[]) => {
            // eslint-disable-next-line @typescript-eslint/ban-ts-comment
            // @ts-ignore
            draft[index][attributeName] = event.target.value;
          }
        );
        setFieldValue("attributes.SurchargePercentage", newItems);
      }
    };

  const V2ChangeListenerBuilder =
    (index: number, setFieldValue: (field: string, value: any) => void) =>
    (attributeName: string) =>
    (event: ChangeEvent<{ value: unknown }>) => {
      if (setFieldValue && index > -1) {
        const newItems = produce(SurchargeV2, (draft: SurchargeV2[]) => {
          // eslint-disable-next-line @typescript-eslint/ban-ts-comment
          // @ts-ignore
          draft[index][attributeName] = event.target.value;
        });
        setFieldValue("attributes.SurchargeV2", newItems);
      }
    };

  const onV1Drop = ({ removedIndex, addedIndex }: DropResult) => {
    if (setFieldValue && removedIndex !== null && addedIndex !== null) {
      const newItems = produce(
        SurchargePercentage,
        (draft: SurchargePercentage[]) => {
          const items = draft.splice(removedIndex, 1);
          draft.splice(addedIndex, 0, ...items);
        }
      );
      setFieldValue("attributes.SurchargePercentage", newItems);
    }
  };

  const onV2Drop = ({ removedIndex, addedIndex }: DropResult) => {
    if (setFieldValue && removedIndex !== null && addedIndex !== null) {
      const newItems = produce(SurchargeV2, (draft: SurchargeV2[]) => {
        const items = draft.splice(removedIndex, 1);
        draft.splice(addedIndex, 0, ...items);
      });
      setFieldValue("attributes.SurchargeV2", newItems);
    }
  };

  const onV1Add = () => {
    if (setFieldValue) {
      setFieldValue(
        "attributes.SurchargePercentage",
        produce(SurchargePercentage, (draft: SurchargePercentage[]) => {
          draft.push({ SurchargeGST: 0, Percentage: 0, CardType: "" });
        })
      );
    }
  };

  const onV2Add = () => {
    if (setFieldValue) {
      setFieldValue(
        "attributes.SurchargeV2",
        produce(SurchargeV2, (draft: SurchargeV2[]) => {
          //console.log(Array.isArray(SurchargeV2));
          draft.push({
            CardType: "",
            SurchargeType: "",
            PurchaseSurchargeValue: 0,
            CashoutSurchargeValue: 0,
            GSTValue: 0,
          });
          setCardTypeInstanceCount(cardTypeInstanceCount + 1);
        })
      );
    }
  };

  const onV1Delete = (index: number) => {
    if (setFieldValue) {
      setFieldValue(
        "attributes.SurchargePercentage",
        produce(SurchargePercentage, (draft: SurchargePercentage[]) => {
          draft.splice(index, 1);
        })
      );
    }
  };

  const onV2Delete = (index: number) => {
    if (setFieldValue) {
      setFieldValue(
        "attributes.SurchargeV2",
        produce(SurchargeV2, (draft: SurchargeV2[]) => {
          draft.splice(index, 1);
          setCardTypeInstanceCount(cardTypeInstanceCount - 1);
        })
      );
    }
  };

  const initialCount = SurchargeV2 ? SurchargeV2.length : 0;

  const [cardTypeInstanceCount, setCardTypeInstanceCount] =
    useState(initialCount);

  const checkedEnableSurchargeV2Mode =
    values &&
    values.attributes &&
    get(values, "attributes.EnableSurchargeV2") === "Yes";

  const checkedEnableSurchargeSettingsOnTerminalMode =
    values &&
    values.attributes &&
    get(values, "attributes.EnableSurchargeSettingsOnTerminal") === "Yes";

  const isDisableFixedSurchargePercentage = Boolean(FixedSurchargeAmount);
  const isDisableFixedSurchargeAmount = Boolean(FixedSurchargePercentage);

  return (
    <>
      <SectionWrapper isChild={true} title="Surcharge and Tipping Version">
        <Grid item xs={12}>
          <FormControl component="fieldset">
            <RadioGroup
              name="attributes.SurchargeV2Toggle"
              value={values.attributes.SurchargeV2Toggle}
              onChange={handleSurchargeV2ToggleOptions}
              row
            >
              {Object.keys(SurchargeV2ToggleOptions).map(
                (SurchargeV2Toggle) => (
                  <CustomizedRadio
                    key={SurchargeV2Toggle}
                    value={SurchargeV2Toggle}
                    label={SurchargeV2ToggleOptions[SurchargeV2Toggle]}
                  />
                )
              )}
            </RadioGroup>
          </FormControl>
        </Grid>
      </SectionWrapper>
      <SectionWrapper isChild={true} title="Surcharge">
        <Grid container xs={12} md={12} spacing={2}>
          {values.attributes.SurchargeV2Toggle === "Yes" ? (
            <>
              <Grid item xs={12} md={12}>
                <Box p={2} pt={0}>
                  <FormControlLabel
                    style={{ marginRight: "3rem" }}
                    control={
                      <CustomizedSwitch
                        name={"attributes.EnableSurchargeV2"}
                        checked={checkedEnableSurchargeV2Mode}
                        onChange={(event) => {
                          const newValue = event.target.checked ? "Yes" : "No";
                          setFieldValue(
                            "attributes.EnableSurchargeV2",
                            newValue
                          );

                          if (newValue === "No") {
                            setFieldValue("attributes.SurchargeTip", false);
                            setFieldValue("attributes.SurchargeGST", false);
                          }
                        }}
                        disabled={!isUpdateAllowed}
                      />
                    }
                    label="Enable surcharge"
                  />
                  <FormControlLabel
                    style={{ marginRight: "3rem" }}
                    control={
                      <CustomizedSwitch
                        checked={values.attributes.SurchargeTip as boolean}
                        onChange={(
                          event: React.ChangeEvent<HTMLInputElement>
                        ) => {
                          setFieldValue(
                            "attributes.SurchargeTip",
                            event.target.checked
                          );

                          // Check if attributes.SurchargeTip is true, then set EnableSurchargeV2 to "Yes"
                          if (event.target.checked) {
                            setFieldValue(
                              "attributes.EnableSurchargeV2",
                              "Yes"
                            );
                          }
                        }}
                      />
                    }
                    label="Surcharge on tips"
                  />
                  <FormControlLabel
                    style={{ marginRight: "3rem" }}
                    control={
                      <CustomizedSwitch
                        checked={values.attributes.SurchargeGST as boolean}
                        onChange={(
                          event: React.ChangeEvent<HTMLInputElement>
                        ) => {
                          setFieldValue(
                            "attributes.SurchargeGST",
                            event.target.checked
                          );

                          if (event.target.checked) {
                            setFieldValue(
                              "attributes.EnableSurchargeV2",
                              "Yes"
                            );
                          }
                        }}
                        disabled={!isUpdateAllowed}
                      />
                    }
                    label="GST on surcharge"
                  />
                  <FormControlLabel
                    control={
                      <CustomizedSwitch
                        name={"attributes.EnableSurchargeSettingsOnTerminal"}
                        checked={checkedEnableSurchargeSettingsOnTerminalMode}
                        onChange={(event, value) => {
                          setFieldValue(
                            "attributes.EnableSurchargeSettingsOnTerminal",
                            getValueForField(
                              "attributes.EnableSurchargeSettingsOnTerminal",
                              value
                            )
                          );
                        }}
                        disabled={!isUpdateAllowed}
                      />
                    }
                    label="Enable surcharge settings on terminal"
                  />
                </Box>

                <hr className={classes.devide} />
              </Grid>

              <Grid item xs={12} md={12}>
                <Box sx={{ display: "flex" }}>
                  <h5 style={{ marginTop: 0 }}>Card types</h5>
                  <OptionalToolTip tooltip="This is only applicable to purchase transactions when bundled rate surcharges are not set">
                    <InfoIcon sx={{ ml: "10px", color: "var(--dmg-blue-accent)" }} />
                  </OptionalToolTip>
                </Box>
                {
                  <OrderedList<SurchargeV2>
                    key={`surcharge-list-v2${Boolean(
                      isDisableSurcharge || !isUpdateAllowed
                    )}`}
                    items={SurchargeV2 || []}
                    itemComponent={(item, index) => (
                      <SurchargeListItemV2
                        key={`surcharge-v2${index}-${Boolean(
                          isDisableSurcharge || !isUpdateAllowed
                        )}`}
                        index={index}
                        surcharge={item}
                        values={values}
                        errors={errors as FormikErrors<Data>}
                        onChange={V2ChangeListenerBuilder(index, setFieldValue)}
                        disabled={
                          values.attributes
                            .EnableSurchargeSettingsOnTerminal === "Yes"
                            ? true
                            : values.attributes.EnableSurchargeV2 === "Yes"
                            ? false
                            : true
                        }
                      />
                    )}
                    onAdd={onV2Add}
                    onDelete={onV2Delete}
                    onDrop={onV2Drop}
                    emptyMessage="No card types defined"
                    disabled={
                      values.attributes.EnableSurchargeSettingsOnTerminal ===
                      "Yes"
                        ? true
                        : values.attributes.EnableSurchargeV2 === "Yes"
                        ? false
                        : true
                    }
                  />
                }
              </Grid>
            </>
          ) : (
            <>
              <Grid item xs={12} md={12}>
                <Box p={2} pt={0}>
                  <FormControlLabel
                    style={{ marginRight: "3rem" }}
                    control={
                      <CustomizedSwitch
                        checked={values.attributes.Surcharge as boolean}
                        onChange={(event) => {
                          setFieldValue(
                            "attributes.Surcharge",
                            event.target.checked
                          );
                        }}
                        disabled={!isUpdateAllowed}
                      />
                    }
                    label="Surcharge"
                  />
                  <FormControlLabel
                    style={{ marginRight: "3rem" }}
                    control={
                      <CustomizedSwitch
                        checked={values.attributes.SurchargeTip as boolean}
                        onChange={(event) => {
                          setFieldValue(
                            "attributes.SurchargeTip",
                            event.target.checked
                          );
                        }}
                        disabled={!isUpdateAllowed}
                      />
                    }
                    label="Surcharge on tips"
                  />
                  <FormControlLabel
                    control={
                      <CustomizedSwitch
                        checked={values.attributes.SurchargeGST as boolean}
                        onChange={(event) => {
                          setFieldValue(
                            "attributes.SurchargeGST",
                            event.target.checked
                          );
                        }}
                        disabled={!isUpdateAllowed}
                      />
                    }
                    label="GST on surcharge"
                  />
                </Box>

                <hr className={classes.devide} />
              </Grid>
              {values.attributes.Surcharge === true ? (
                <>
                  <Grid item xs={12} md={12}>
                    <h5 style={{ marginTop: 0 }}>Card types</h5>
                    {
                      <OrderedList<SurchargePercentage>
                        key={`surcharge-list-${Boolean(
                          isDisableSurcharge || !isUpdateAllowed
                        )}`}
                        items={SurchargePercentage || []}
                        itemComponent={(item, index) => (
                          <SurchargeListItem
                            key={`surcharge-${index}-${Boolean(
                              isDisableSurcharge || !isUpdateAllowed
                            )}`}
                            index={index}
                            surcharge={item}
                            values={values}
                            errors={errors as FormikErrors<Data>}
                            onChange={V1ChangeListenerBuilder(
                              index,
                              setFieldValue
                            )}
                          />
                        )}
                        onAdd={onV1Add}
                        onDelete={onV1Delete}
                        onDrop={onV1Drop}
                        emptyMessage="No card types defined"
                      />
                    }
                  </Grid>
                </>
              ) : (
                <>
                  <Grid item xs={12} md={12}>
                    <h5 style={{ marginTop: 0 }}>Card types</h5>
                    {
                      <OrderedList<SurchargePercentage>
                        key={`surcharge-list-${Boolean(
                          isDisableSurcharge || !isUpdateAllowed
                        )}`}
                        items={SurchargePercentage || []}
                        itemComponent={(item, index) => (
                          <SurchargeListItem
                            key={`surcharge-${index}-${Boolean(
                              isDisableSurcharge || !isUpdateAllowed
                            )}`}
                            index={index}
                            surcharge={item}
                            values={values}
                            errors={errors as FormikErrors<Data>}
                            onChange={V1ChangeListenerBuilder(
                              index,
                              setFieldValue
                            )}
                            disabled
                          />
                        )}
                        onAdd={onV1Add}
                        onDelete={onV1Delete}
                        onDrop={onV1Drop}
                        emptyMessage="No card types defined"
                        disabled={isDisableSurcharge || !isUpdateAllowed}
                      />
                    }
                  </Grid>
                </>
              )}
            </>
          )}
        </Grid>
      </SectionWrapper>
      {values.attributes.SurchargeV2Toggle === "Yes" ? (
        <>
          <SectionWrapper
            isChild={true}
            title="Surcharge Sale Amount Thresholds"
            icon={
              <OptionalToolTip tooltip="Minimum and maximum transaction values to apply surcharges against">
                <InfoIcon sx={{ ml: "10px", color: "var(--dmg-blue-accent)" }} />
              </OptionalToolTip>
            }
          >
            <Grid container spacing={2} xs={12}>
              <Grid item xs={12} md={3}>
                <TextInput
                  fieldname="attributes.SurchargeMinSaleAmount"
                  label="Surcharge Min Sale"
                  disabled={!isUpdateAllowed}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">$</InputAdornment>
                    ),
                  }}
                />
              </Grid>
              <Grid item xs={12} md={3}>
                <TextInput
                  fieldname="attributes.SurchargeMaxSaleAmount"
                  label="Surcharge Max Sale"
                  disabled={!isUpdateAllowed}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">$</InputAdornment>
                    ),
                  }}
                />
              </Grid>
            </Grid>
          </SectionWrapper>
          <TippingV2 isUpdateAllowed={isUpdateAllowed} />
          <SectionWrapper
            isChild={true}
            title="Disclaimer"
            icon={
              <OptionalToolTip tooltip="Settings to enable/disable a custom surcharge disclaimer on terminal">
                <InfoIcon sx={{ ml: "10px", color: "var(--dmg-blue-accent)" }} />
              </OptionalToolTip>
            }
          >
            <>
              <Grid item xs={12} md={12}>
                <Box p={2} pt={0}>
                  <FormControlLabel
                    style={{ marginRight: "3rem" }}
                    control={
                      <CustomizedSwitch
                        checked={
                          values.attributes.SurchargeShowDisclaimer as boolean
                        }
                        onChange={(event) => {
                          setFieldValue(
                            "attributes.SurchargeShowDisclaimer",
                            event.target.checked
                          );
                        }}
                        disabled={!isUpdateAllowed}
                      />
                    }
                    label="Enable Surcharge Disclaimer"
                  />
                </Box>
              </Grid>
              <Grid item xs={12} md={3}>
                <TextInput
                  fieldname="attributes.SurchargeDisclaimerText"
                  label="Surcharge Disclaimer Text"
                  disabled={!isUpdateAllowed}
                />
              </Grid>
            </>
          </SectionWrapper>
          <SectionWrapper
            isChild={true}
            title="Surcharge Bundled Rate (All Schemes)"
            icon={
              <OptionalToolTip tooltip="This is only applicable to purchase transactions when scheme specific surcharges are not set">
                <InfoIcon sx={{ ml: "10px", color: "var(--dmg-blue-accent)" }} />
              </OptionalToolTip>
            }
          >
            <Grid container spacing={2} xs={12}>
              <Grid item xs={12} md={3}>
                <OptionalToolTip tooltip="Adds a percentage surcharge to each transaction across all card types">
                  <TextInput
                    key={`FixedSurchargePercentage_${isDisableFixedSurchargePercentage}_${cardTypeInstanceCount}`}
                    type="number"
                    fieldname="attributes.FixedSurchargePercentage"
                    label="Percentage"
                    disabled={
                      cardTypeInstanceCount > 0 ||
                      isDisableFixedSurchargePercentage ||
                      !isUpdateAllowed
                    }
                    InputProps={{
                      endAdornment: (
                        <InputAdornment position="end">%</InputAdornment>
                      ),
                    }}
                  />
                </OptionalToolTip>
              </Grid>
              <Grid item xs={12} md={3}>
                <OptionalToolTip tooltip="Adds a fixed surcharge rate to each transaction across all card types">
                  <TextInput
                    key={`FixedSurchargeAmount_${isDisableFixedSurchargeAmount}_${cardTypeInstanceCount}`}
                    type="number"
                    fieldname="attributes.FixedSurchargeAmount"
                    label="Amount"
                    disabled={
                      cardTypeInstanceCount > 0 ||
                      isDisableFixedSurchargeAmount ||
                      !isUpdateAllowed
                    }
                    InputProps={{
                      endAdornment: (
                        <InputAdornment position="end">$</InputAdornment>
                      ),
                    }}
                  />
                </OptionalToolTip>
              </Grid>
            </Grid>
          </SectionWrapper>
        </>
      ) : (
        <>
          <Tipping isUpdateAllowed={isUpdateAllowed} />
          <SectionWrapper
            isChild={true}
            title="Surcharge Bundled Rate (All Schemes)"
            icon={
              <OptionalToolTip tooltip="This is only applicable to purchase transactions when scheme specific surcharges are not set">
                <InfoIcon sx={{ ml: "10px", color: "var(--dmg-blue-accent)" }} />
              </OptionalToolTip>
            }
          >
            <Grid container spacing={2} xs={12}>
              <Grid item xs={12} md={3}>
                <OptionalToolTip tooltip="Adds a percentage surcharge to each transaction across all card types">
                  <TextInput
                    key={`FixedSurchargePercentage+${isDisableFixedSurchargePercentage}`}
                    type="number"
                    fieldname="attributes.FixedSurchargePercentage"
                    label="Percentage"
                    disabled={
                      isDisableFixedSurchargePercentage || !isUpdateAllowed
                    }
                    InputProps={{
                      endAdornment: (
                        <InputAdornment position="end">%</InputAdornment>
                      ),
                    }}
                  />
                </OptionalToolTip>
              </Grid>
              <Grid item xs={12} md={3}>
                <OptionalToolTip tooltip="Adds a fixed surcharge rate to each transaction across all card types">
                  <TextInput
                    key={`FixedSurchargeAmount+${isDisableFixedSurchargeAmount}`}
                    type="number"
                    fieldname="attributes.FixedSurchargeAmount"
                    label="Amount"
                    disabled={isDisableFixedSurchargeAmount || !isUpdateAllowed}
                    InputProps={{
                      endAdornment: (
                        <InputAdornment position="end">$</InputAdornment>
                      ),
                    }}
                  />
                </OptionalToolTip>
              </Grid>
            </Grid>
          </SectionWrapper>
        </>
      )}
    </>
  );
};

const ConnectedSurcharge = connect(
  null,
  null,
  (_stateProps, _dispatchProps, ownProps: SurchargeProps) => {
    return {
      ...ownProps,
    };
  }
)(Surcharge);

const ConnectedSurchargeHoc = (props: SurchargeProps) => (
  // @ts-ignore
  <ConfigPropExtenderHoc propName="toolTips">
    <ConnectedSurcharge {...props} />
  </ConfigPropExtenderHoc>
);

export default ConnectedSurchargeHoc;
